<template>
  <div @click="show_search=false" class="h-20 bg-white border-b border-gray-100 flex items-center justify-between px-8 sticky top-0 z-50 animate__animated animate__fadeIn">
    <!-- Top Accent Bar -->
    <div class="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0d47a1] to-blue-400 opacity-80"></div>

    <!-- Left Section: Search -->
    <div class="flex-1 max-w-xl relative">
      <div v-if="me.id && me.is_admin" class="relative group">
        <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 group-focus-within:text-[#0d47a1] transition-colors">
          <i class="material-icons text-xl">search</i>
        </span>
        <input 
          @click.stop="" 
          @focusin="show_search=true" 
          v-model="search_query" 
          @input="debounceSearch" 
          class="block w-full pl-10 pr-12 py-2.5 border border-transparent bg-gray-50 rounded-xl leading-5 text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-[#0d47a1]/10 focus:border-[#0d47a1] sm:text-sm transition-all duration-200" 
          type="text" 
          placeholder="Quick search (employees, payrolls...)"
        />
        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          <kbd class="hidden sm:inline-flex items-center px-1.5 py-0.5 border border-gray-200 rounded text-[10px] font-black text-gray-400 bg-white uppercase">⌘K</kbd>
        </div>
      </div>
    </div>

    <!-- Right Section: Actions & Info -->
    <div class="flex items-center space-x-3">
      <div class="flex items-center space-x-1 pr-4 border-r border-gray-100 relative">
        <button 
          @click.stop="show_notifications = !show_notifications; show_search = false"
          class="p-2 text-gray-400 hover:text-[#0d47a1] transition-all rounded-lg hover:bg-blue-50 relative group"
        >
          <i class="material-icons text-2xl">notifications_none</i>
          <span class="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
          <span class="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[10px] font-black rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">Notifications</span>
        </button>

        <!-- Notifications Dropdown -->
        <div v-if="show_notifications" class="absolute top-14 right-0 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-[70] animate__animated animate__fadeInDown animate__faster">
          <div class="px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex justify-between items-center">
            <span class="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Activity Center</span>
            <button @click="show_notifications = false" class="text-[10px] font-black text-[#0d47a1] uppercase hover:underline">Clear All</button>
          </div>
          <div class="p-10 text-center">
            <div class="h-16 w-16 bg-blue-50 text-[#0d47a1]/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i class="material-icons text-3xl">notifications_off</i>
            </div>
            <p class="text-sm font-black text-gray-900 mb-1">No new alerts</p>
            <p class="text-[10px] text-gray-400 font-medium leading-relaxed">Your organization is fully up to date. New payroll requests or system alerts will appear here.</p>
          </div>
          <div class="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
            <button class="text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-[#0d47a1] transition-colors">View All History</button>
          </div>
        </div>

        <button class="p-2 text-gray-400 hover:text-[#0d47a1] transition-all rounded-lg hover:bg-blue-50 group relative">
          <i class="material-icons text-2xl">help_outline</i>
          <span class="absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[10px] font-black rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50">Support</span>
        </button>
      </div>
      
      <div v-if="me.id" class="flex items-center space-x-3 pl-4">
        <div class="text-right hidden md:block">
          <p class="text-sm font-black text-gray-900 leading-tight">{{ me.first_name }} {{ me.last_name }}</p>
          <p class="text-[10px] font-bold text-[#0d47a1] uppercase tracking-widest">{{ me.organization || 'Administrator' }}</p>
        </div>
        <div class="relative group cursor-pointer" @click="$router.push('/employee/'+me.id)">
          <div class="h-10 w-10 rounded-xl overflow-hidden border-2 border-white shadow-sm ring-1 ring-gray-100 group-hover:ring-[#0d47a1] transition-all">
            <img :src="me.profile_pic || '/images/profile.jpg'" class="h-full w-full object-cover"/>
          </div>
        </div>
      </div>
    </div>

    <!-- Search Results Overlay -->
    <div v-if="show_search && employees.length" class="fixed top-20 left-0 w-full h-full bg-gray-900/10 backdrop-blur-[2px] z-[60]" @click="show_search=false">
      <div @click.stop="" class="mx-auto mt-4 max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100 max-h-[60vh] flex flex-col animate__animated animate__fadeInUp animate__faster">
        <div class="px-6 py-4 border-b border-gray-50 bg-gray-50/50 flex justify-between items-center">
          <span class="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Search Results ({{ employees.length }})</span>
          <button @click="show_search=false" class="p-1 hover:bg-gray-100 rounded-full transition-colors"><i class="material-icons text-sm text-gray-400">close</i></button>
        </div>
        <div class="overflow-y-auto custom-scrollbar">
          <div v-for="emp in employees" :key="emp.id" @click="toEmp('/employee/'+emp.id); show_search=false" class="px-6 py-4 flex items-center justify-between hover:bg-blue-50/50 cursor-pointer border-b border-gray-50 last:border-0 transition-all group">
            <div class="flex items-center space-x-4">
              <div class="h-10 w-10 rounded-lg bg-[#0d47a1]/5 flex items-center justify-center text-[#0d47a1] font-black border border-[#0d47a1]/10 group-hover:bg-[#0d47a1] group-hover:text-white transition-all">
                {{ emp.first_name[0] }}{{ emp.last_name[0] }}
              </div>
              <div>
                <p class="text-sm font-black text-gray-900 leading-tight">{{ emp.first_name }} {{ emp.last_name }}</p>
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 group-hover:text-gray-500">{{ emp.position || 'Employee' }} • @{{ emp.username }}</p>
              </div>
            </div>
            <i class="material-icons text-gray-300 group-hover:text-[#0d47a1] transition-colors">chevron_right</i>
          </div>
        </div>
        <div class="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
          <p class="text-[10px] font-black text-gray-400 uppercase tracking-widest">End of results</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import get_api from "../service/api.js"
import base_url from "../service/base_url.js"

export default {
  name: "NavBar",
  created() {
    this.api = get_api(this.$router)
    if (localStorage.getItem("XPAYROLLaccess_token")){
      this.api.get(`${base_url}/me`).then((resp)=>{
        this.me = resp.data.data
      })
    }
  },
  data(){
    return {
      me: {},
      show_search: false,
      show_notifications: false,
      search_query: "",
      employees: [],
      checks: []
    }
  },
  methods: {
    logout(){
      localStorage.removeItem("XPAYROLLaccess_token")
      this.$store.commit('set_access_token', "")
      window.location.reload()
    },
    search_employees(){
      if (!this.search_query) {
        this.employees = []
        return
      }
      this.api.get(base_url+"/employees?s="+this.search_query).then((resp)=>{
        this.employees = resp.data.data
      })
    },
    debounceSearch() {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.search_employees();
      }, 200); 
    },
    toEmp(href){
      window.location.href = href
    }
  },
  computed: {
    no_auth() {
      return !localStorage.getItem('XPAYROLLaccess_token')
    }
  },
  watch:{
    search_query(){
      this.debounceSearch()
    }
  }
};
</script>

<style scoped>
.animate__animated.animate__fadeIn {
  --animate-duration: 0.3s;
}
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #d1d5db;
}
</style>